/**
 * Service d'authentification centralisé
 * Gère l'authentification, l'autorisation et la synchronisation des données utilisateur
 */

import axios from 'axios';
import { TokenManager } from './token-manager';
import UserDataManager from './user-data-manager';
import { UserProfile, UserRole, AuthTokens } from '../types';

// Types et interfaces
interface JwtToken {
  exp: number;
  user_id: string;
  username?: string;
  email?: string;
  is_superuser?: boolean;
  is_staff?: boolean;
  tenants?: any[];
  tenant_memberships?: any[];
  active_features?: string[];
  [key: string]: any;
}

interface ApiError {
  response?: {
    status?: number;
    data?: any;
  };
  message?: string;
}

interface LoginCredentials {
  username: string;
  password: string;
}

interface AuthOptions {
  baseUrl: string;
  apiPrefix: string;
  withCredentials: boolean;
  defaultTimeout: number;
}

// URLs des endpoints d'authentification
const AUTH_ENDPOINTS = {
  LOGIN: '/api/token/',
  REFRESH: '/api/token/refresh/',
  LOGOUT: '/api/token/logout/',
  CURRENT_USER: '/api/users/me/',
  USER_PROFILE: '/api/user/profile/',
  TENANTS_USER_DATA: '/api/tenants/user-data/'
};

/**
 * Fonction utilitaire pour décoder un token JWT sans vérification
 * @param token Le token JWT à décoder
 * @returns Le contenu décodé du token ou null en cas d'erreur
 */
function jwtDecode<T>(token: string): T | null {
  try {
    // Diviser le token en 3 parties (header, payload, signature)
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Format de token JWT invalide');
    }
    
    // Décoder la partie payload (2ème partie)
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload) as T;
  } catch (error) {
    console.error('Erreur lors du décodage du token JWT:', error);
    return null;
  }
}

class AuthService {
  private options: AuthOptions;
  private authListeners: ((isAuthenticated: boolean) => void)[] = [];

  constructor(options: Partial<AuthOptions> = {}) {
    // Valeurs par défaut combinées avec les options fournies
    this.options = {
      baseUrl: '/api',
      apiPrefix: '',
      withCredentials: true,
      defaultTimeout: 10000,
      ...options
    };

    // Initialiser le service lors de la création
    this.initialize();
  }

  /**
   * Initialise le service d'authentification
   */
  private initialize(): void {
    console.log('🔐 Initialisation du service d\'authentification');
    
    // Vérifier l'authentification actuelle au démarrage
    const isAuthenticated = TokenManager.isAuthenticated();
    console.log(`🔐 État d'authentification initial: ${isAuthenticated ? 'Authentifié' : 'Non authentifié'}`);
    
    // Synchroniser les données utilisateur si authentifié
    if (isAuthenticated) {
      this.synchronizeUserData();
    }
  }

  /**
   * Vérifie si l'utilisateur est authentifié
   */
  isAuthenticated(): boolean {
    return TokenManager.isAuthenticated();
  }

  /**
   * Synchronise les données utilisateur entre TokenManager et UserDataManager
   */
  private synchronizeUserData(): void {
    try {
      const userProfile = TokenManager.getUserProfile();
      if (userProfile) {
        UserDataManager.setUserDataCookie(userProfile);
        UserDataManager.saveUserDataToLocalStorage(userProfile);
      }
    } catch (error) {
      console.warn('Erreur lors de la synchronisation des données utilisateur:', error);
    }
  }

  /**
   * Vérifie si un objet est un profil utilisateur valide
   */
  private isValidUserProfile(profile: any): boolean {
    if (!profile) return false;
    
    // Vérifier si l'objet a un ID utilisateur
    if (typeof profile.id !== 'number' && typeof profile.id !== 'string') {
      return false;
    }
    
    // Vérifier que ce n'est pas un tableau ou un objet de type pagination
    if (Array.isArray(profile) || 'results' in profile || 'count' in profile) {
      return false;
    }
    
    return true;
  }

  /**
   * Extrait les données utilisateur à partir d'un token JWT
   */
  private extractUserDataFromToken(token: string): UserProfile | null {
    try {
      const decodedToken = jwtDecode<JwtToken>(token);
      if (!decodedToken) return null;
      
      // Déterminer le rôle utilisateur
      let userRole: UserRole;
      if (decodedToken.is_superuser) {
        userRole = UserRole.ADMIN;
      } else if (decodedToken.is_staff) {
        userRole = UserRole.MANAGER;
      } else {
        userRole = UserRole.USER;
      }
      
      // Extraire le nom d'utilisateur
      const username = decodedToken.username 
        || decodedToken.preferred_username 
        || decodedToken.name 
        || decodedToken.email?.split('@')[0] 
        || 'Utilisateur';
      
      // Extraire l'email
      const email = decodedToken.email 
        || decodedToken.upn 
        || (decodedToken.sub && decodedToken.sub.includes('@') ? decodedToken.sub : null)
        || `${username}@cyber-threat-consulting.com`;
      
      // Construire le profil utilisateur
      const userProfile: UserProfile = {
        id: decodedToken.user_id,
        username: username,
        email: email,
        role: userRole,
        is_active: true,
        tenants: decodedToken.tenants || [],
        tenant_memberships: decodedToken.tenant_memberships || [],
        active_features: decodedToken.active_features || [],
        // Ajouter d'autres champs disponibles dans le token
        ...decodedToken
      };
      
      return userProfile;
    } catch (error) {
      console.error('Erreur lors de l\'extraction des données utilisateur du token:', error);
      return null;
    }
  }

  /**
   * Récupère les données utilisateur depuis l'API
   */
  private async fetchUserDataFromAPI(accessToken: string): Promise<UserProfile | null> {
    // Endpoints à essayer pour récupérer les données utilisateur
    const endpoints = [
      `${this.options.baseUrl}${AUTH_ENDPOINTS.CURRENT_USER}`,
      `${this.options.baseUrl}${AUTH_ENDPOINTS.USER_PROFILE}`,
      `${this.options.baseUrl}${AUTH_ENDPOINTS.TENANTS_USER_DATA}`
    ];
    
    for (const endpoint of endpoints) {
      try {
        console.log(`🔐 Tentative de récupération des données utilisateur depuis ${endpoint}`);
        
        const response = await axios.get(endpoint, {
          headers: {
            'Authorization': `Token ${accessToken}`,
            'Content-Type': 'application/json'
          },
          withCredentials: this.options.withCredentials,
          timeout: this.options.defaultTimeout
        });
        
        if (response.status === 200 && response.data) {
          console.log(`🔐 Données utilisateur récupérées depuis ${endpoint}`, response.data);
          
          // Si c'est l'endpoint TENANTS_USER_DATA, la structure est différente
          if (endpoint.includes('tenants/user-data')) {
            if (response.data.authenticated === false) {
              console.warn('🔐 Utilisateur non authentifié selon l\'API');
              return null;
            }
            return response.data.user as UserProfile;
          }
          
          return response.data as UserProfile;
        }
      } catch (error) {
        console.warn(`🔐 Erreur lors de la récupération des données utilisateur depuis ${endpoint}:`, error);
      }
    }
    
    return null;
  }

  /**
   * Tente de rafraîchir le token d'accès
   */
  private async attemptTokenRefresh(): Promise<boolean> {
    try {
      const refreshToken = TokenManager.getRefreshToken();
      if (!refreshToken) {
        console.warn('🔐 Aucun refresh token disponible');
        return false;
      }
      
      console.log('🔐 Tentative de rafraîchissement du token');
      
      const response = await axios.post(
        `${this.options.baseUrl}${AUTH_ENDPOINTS.REFRESH}`,
        { refresh: refreshToken },
        {
          withCredentials: this.options.withCredentials,
          timeout: this.options.defaultTimeout
        }
      );
      
      if (response.data?.access) {
        console.log('🔐 Token rafraîchi avec succès');
        
        // Mettre à jour les tokens
        TokenManager.setTokens({
          access_token: response.data.access,
          refresh_token: refreshToken,
          expires_at: Date.now() + 30 * 60 * 1000 // 30 minutes par défaut
        });
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('🔐 Erreur lors du rafraîchissement du token:', error);
      return false;
    }
  }

  /**
   * Récupère l'utilisateur actuel
   */
  async getCurrentUser(): Promise<UserProfile | null> {
    try {
      // Vérifier l'authentification
      if (!TokenManager.isAuthenticated()) {
        console.warn('🔐 Utilisateur non authentifié selon TokenManager');
        return null;
      }
      
      // Utiliser d'abord le profil en cache si disponible et valide
      const cachedProfile = TokenManager.getUserProfile();
      if (cachedProfile && this.isValidUserProfile(cachedProfile)) {
        console.log('🔐 Utilisation du profil utilisateur en cache');
        return cachedProfile;
      }
      
      // Récupérer le token d'accès
      const accessToken = TokenManager.getAccessToken();
      if (!accessToken) {
        // Tenter un rafraîchissement automatique
        const refreshSuccessful = await this.attemptTokenRefresh();
        if (!refreshSuccessful) {
          console.warn('🔐 Impossible de rafraîchir le token');
          return null;
        }
        return this.getCurrentUser(); // Réessayer après rafraîchissement
      }
      
      // Récupérer les données utilisateur depuis l'API
      const userData = await this.fetchUserDataFromAPI(accessToken);
      if (userData) {
        console.log('🔐 Données utilisateur récupérées depuis l\'API');
        TokenManager.setUserProfile(userData);
        this.synchronizeUserData();
        return userData;
      }
      
      // Fallback: extraire les données du token JWT
      console.log('🔐 Tentative d\'extraction des données utilisateur du token JWT');
      const tokenData = this.extractUserDataFromToken(accessToken);
      if (tokenData) {
        console.log('🔐 Données utilisateur extraites du token JWT');
        TokenManager.setUserProfile(tokenData);
        this.synchronizeUserData();
        return tokenData;
      }
      
      console.warn('🔐 Impossible de récupérer les données utilisateur');
      return null;
    } catch (error) {
      console.error('🔐 Erreur lors de la récupération des données utilisateur:', error);
      return null;
    }
  }

  /**
   * Rafraîchit le token d'accès
   */
  async refreshToken(): Promise<boolean> {
    return this.attemptTokenRefresh();
  }

  /**
   * Connecte l'utilisateur avec ses identifiants
   */
  async login(credentials: LoginCredentials): Promise<UserProfile | null> {
    try {
      console.log('🔐 Tentative de connexion pour', credentials.username);
      
      // Valider les identifiants
      if (!credentials.username || !credentials.password) {
        throw new Error('Identifiants incomplets');
      }
      
      // Déterminer si le nom d'utilisateur est un email
      const isEmail = credentials.username.includes('@');
      
      // Créer la charge utile avec email ou username selon le format
      const payload = {
        username: credentials.username,
        email: isEmail ? credentials.username : undefined,
        password: credentials.password
      };
      
      // Endpoints de connexion à essayer
      const loginEndpoints = [
        `${this.options.baseUrl}${AUTH_ENDPOINTS.LOGIN}`,
        `${this.options.baseUrl}/auth/token/`,
        `${this.options.baseUrl}/token/`
      ];
      
      let response = null;
      let lastError = null;
      
      // Essayer chaque endpoint
      for (const endpoint of loginEndpoints) {
        try {
          console.log(`🔐 Tentative de connexion avec l'endpoint: ${endpoint}`);
          
          response = await axios.post(
            endpoint,
            payload,
            {
              withCredentials: this.options.withCredentials,
              timeout: this.options.defaultTimeout,
              headers: {
                'Content-Type': 'application/json'
              }
            }
          );
          
          // Si on arrive ici, la requête a réussi
          console.log(`🔐 Connexion réussie avec l'endpoint: ${endpoint}`);
          break;
        } catch (error: any) {
          console.warn(`🔐 Échec de la connexion avec l'endpoint: ${endpoint}`, error);
          lastError = error;
        }
      }
      
      // Si aucun endpoint n'a fonctionné
      if (!response) {
        throw lastError || new Error('Échec de la connexion avec tous les endpoints');
      }
      
      // Traiter la réponse
      if (!response.data) {
        throw new Error('Aucune donnée reçue après la connexion');
      }
      
      // Gestion de la réponse JWT
      if ('access' in response.data) {
        console.log('🔐 Réponse JWT reçue');
        
        // Stocker les tokens
        const tokenData: AuthTokens = {
          access_token: response.data.access,
          refresh_token: response.data.refresh || '',
          expires_at: Date.now() + (response.data.expires_in || 3600) * 1000
        };
        
        TokenManager.setTokens(tokenData);
        
        // Récupérer les données utilisateur
        const user = await this.getCurrentUser();
        if (!user) {
          throw new Error('Impossible de récupérer les données utilisateur après connexion');
        }
        
        // Notifier les écouteurs
        this.notifyAuthListeners(true);
        
        return user;
      }
      
      // Gestion de la réponse avec données utilisateur
      if ('user' in response.data) {
        console.log('🔐 Réponse avec données utilisateur reçue');
        
        if (response.data.tokens) {
          // Stocker les tokens
          const tokenData: AuthTokens = {
            access_token: response.data.tokens.access_token,
            refresh_token: response.data.tokens.refresh_token || '',
            expires_at: Date.now() + (response.data.expires_in || 3600) * 1000
          };
          
          TokenManager.setTokens(tokenData);
        }
        
        // Stocker le profil utilisateur
        TokenManager.setUserProfile(response.data.user);
        this.synchronizeUserData();
        
        // Notifier les écouteurs
        this.notifyAuthListeners(true);
        
        return response.data.user;
      }
      
      throw new Error('Format de réponse non reconnu');
    } catch (error) {
      console.error('🔐 Erreur lors de la connexion:', error);
      throw error;
    }
  }

  /**
   * Déconnecte l'utilisateur
   */
  async logout(): Promise<void> {
    try {
      console.log('🔐 Début du processus de déconnexion');
      
      // Récupérer les tokens
      const refreshToken = TokenManager.getRefreshToken();
      const accessToken = TokenManager.getAccessToken();
      
      // Si des tokens sont disponibles, essayer d'invalider côté serveur
      if (refreshToken && accessToken) {
        // Endpoints de déconnexion à essayer
        const logoutEndpoints = [
          `${this.options.baseUrl}${AUTH_ENDPOINTS.LOGOUT}`,
          `${this.options.baseUrl}/auth/logout/`,
          `${this.options.baseUrl}/logout/`
        ];
        
        // Essayer chaque endpoint sans bloquer
        const logoutPromises = logoutEndpoints.map(endpoint => {
          return axios.post(
            endpoint,
            { refresh: refreshToken },
            {
              headers: {
                'Authorization': `Token ${accessToken}`,
                'Content-Type': 'application/json'
              },
              timeout: 5000,
              withCredentials: this.options.withCredentials
            }
          ).catch(e => null); // Ignorer les erreurs
        });
        
        // Attendre que toutes les requêtes soient terminées
        await Promise.allSettled(logoutPromises);
      }
      
      // Nettoyer les données côté client
      TokenManager.clearTokens();
      UserDataManager.clearUserData();
      
      // Vérification supplémentaire
      setTimeout(() => {
        if (TokenManager.getAccessToken() || TokenManager.getRefreshToken()) {
          console.warn('🔐 Certaines données sont toujours présentes, nettoyage agressif');
          TokenManager.clearTokens();
          UserDataManager.clearUserData();
          
          // Nettoyer directement les cookies
          if (typeof document !== 'undefined') {
            document.cookie = 'sentinel_auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            document.cookie = 'sentinel_refresh_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            document.cookie = 'sentinel_user_profile=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            document.cookie = 'user-data=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
          }
        }
      }, 200);
      
      // Notifier les écouteurs
      this.notifyAuthListeners(false);
      
      // Redirection après déconnexion
      if (typeof window !== 'undefined') {
        setTimeout(() => {
          window.location.href = `/?logout=${Date.now()}`;
        }, 500);
      }
    } catch (error) {
      console.error('🔐 Erreur lors de la déconnexion:', error);
      
      // Même en cas d'erreur, nettoyer les données
      TokenManager.clearTokens();
      UserDataManager.clearUserData();
      this.notifyAuthListeners(false);
    }
  }

  /**
   * Ajoute un écouteur d'événements d'authentification
   */
  addAuthListener(listener: (isAuthenticated: boolean) => void): void {
    this.authListeners.push(listener);
  }

  /**
   * Supprime un écouteur d'événements d'authentification
   */
  removeAuthListener(listener: (isAuthenticated: boolean) => void): void {
    this.authListeners = this.authListeners.filter(l => l !== listener);
  }

  /**
   * Notifie tous les écouteurs d'un changement d'état d'authentification
   */
  private notifyAuthListeners(isAuthenticated: boolean): void {
    this.authListeners.forEach(listener => {
      try {
        listener(isAuthenticated);
      } catch (error) {
        console.error('🔐 Erreur dans un écouteur d\'authentification:', error);
      }
    });
  }

  /**
   * Vérifie si l'utilisateur a une permission spécifique
   */
  hasPermission(permission: string): boolean {
    try {
      const userProfile = TokenManager.getUserProfile();
      if (!userProfile) return false;
      
      // Si l'utilisateur est admin, il a toutes les permissions
      if (userProfile.role === UserRole.ADMIN || userProfile.is_superuser) {
        return true;
      }
      
      // Vérifier les permissions explicites
      if (userProfile.permissions && Array.isArray(userProfile.permissions)) {
        if (userProfile.permissions.includes(permission)) {
          return true;
        }
      }
      
      // Vérifier les fonctionnalités actives
      if (userProfile.active_features && Array.isArray(userProfile.active_features)) {
        if (userProfile.active_features.includes(permission)) {
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('🔐 Erreur lors de la vérification des permissions:', error);
      return false;
    }
  }

  /**
   * Vérifie si l'utilisateur a un rôle spécifique
   */
  hasRole(role: UserRole): boolean {
    try {
      const userProfile = TokenManager.getUserProfile();
      if (!userProfile) return false;
      
      // Si l'utilisateur est admin, il a tous les rôles
      if (userProfile.role === UserRole.ADMIN || userProfile.is_superuser) {
        return true;
      }
      
      // Vérifier le rôle exact
      return userProfile.role === role;
    } catch (error) {
      console.error('🔐 Erreur lors de la vérification du rôle:', error);
      return false;
    }
  }
}

// Créer une instance unique pour toute l'application
const authService = new AuthService();

// Initialiser le service au chargement du navigateur
if (typeof window !== 'undefined') {
  // S'assurer que le service est initialisé après le chargement complet
  if (document.readyState === 'complete') {
    authService.getCurrentUser();
  } else {
    window.addEventListener('load', () => {
      authService.getCurrentUser();
    });
  }
}

export { authService, AuthService };
